import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, Loading } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import CardGridControl from "../../CardGridControl";
import {
  buildDestinationDeploymentSavePayload,
  buildEntityDeploymentFormMetadata,
  buildEntityDeploymentStepValues,
  createClientGuid,
  getDeploymentDisplayName,
  getDeploymentRecordId,
  getDeploymentOptions,
  getDestinationDeploymentRows,
  getDestinationIdFromValues,
  getDestinationLabel,
  getDestinationManagerKey,
  getDestinationRowKey,
  getDestinationSetupDefaults,
  getEntityDeploymentRows,
  getEntityObjectDisplayName,
  getEntityObjectOptions,
  getEntityObjectRecordId,
  getSavedEntityDeploymentStepValues,
  hasLoadedManagerMetadata,
  isCoreProfileMetadataReady,
  loadDestinationById,
  loadDestinationManagerMetadata,
  normalizeEntityDeploymentRow,
  normalizeDeploymentRow,
  resolveMetadataItem,
  saveDestination,
  setEntityDeploymentRows,
  validateEntityDeploymentRows
} from "./destinationSetupStepUtils";
import {
  asArray,
  buildWizardValidationError,
  getMetadataFields,
  getOperationItems,
  readValue,
  requireServiceResponse,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const ENTITY_OBJECT_DEPLOYMENT_ITEM_NAME = "EntityObjectDeployment";
const ENTITY_SELECTOR_METADATA = {
  Fields: [
    { FieldName: "EntitySelectorName", DisplayName: "Entity Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 1 },
    { FieldName: "EntitySelectorObject", DisplayName: "Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 2 },
    { FieldName: "EntitySelectorSourceObjectOfRecord", DisplayName: "Source Object of Record", ShowOnCard: true, ShowOnGrid: true, GridSequence: 3 }
  ]
};

const pairKey = (row) => `${readValue(row, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId") || ""}:${readValue(row, "EntityObjectId", "entityObjectId") || ""}`;
const normalizeKey = (value) => String(value || "").trim();
const getDefaultDeployment = (deployments = []) =>
  asArray(deployments).find((row) => readValue(row, "IsDeleted", "isDeleted") !== true && readValue(row, "IsActive", "isActive") !== false)
  || asArray(deployments).find((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
  || asArray(deployments)[0]
  || null;
const SOURCE_OBJECT_SELECT_DATA_FIELD = {
  FieldName: "SourceObjectOfRecordId",
  DisplayName: "Source Object of Record",
  FieldType: SystemTypes.UIFieldType.SelectList,
  DataOperationItem: "SourceObject"
};
const getEntityObjectSourceObjectOfRecordId = (entityObject) =>
  normalizeKey(readValue(entityObject, "SourceObjectOfRecordId", "sourceObjectOfRecordId", "SourceObjectId", "sourceObjectId"));
const getSourceObjectDisplayName = (sourceObject) =>
  readValue(sourceObject, "DisplayName", "displayName", "Text", "text", "Name", "name", "SystemName", "systemName", "ObjectName", "objectName") || "";
const getSourceObjectRecordIds = (sourceObject) => [
  readValue(sourceObject, "_id", "id"),
  readValue(sourceObject, "Value", "value"),
  readValue(sourceObject, "UniqueId", "uniqueId"),
  readValue(sourceObject, "SourceObjectId", "sourceObjectId", "EntityObjectId", "entityObjectId")
].map(normalizeKey).filter(Boolean);
const buildSourceObjectsById = (sourceObjects = []) => {
  const sourceObjectsById = new Map();
  asArray(sourceObjects).forEach((sourceObject) => {
    const displayName = getSourceObjectDisplayName(sourceObject);
    if (!displayName) return;
    getSourceObjectRecordIds(sourceObject).forEach((id) => sourceObjectsById.set(id.toLowerCase(), displayName));
  });
  return sourceObjectsById;
};
const getEntityObjectSourceObjectOfRecordDisplayName = (entityObject, sourceObjectsById = new Map()) => {
  const directName = readValue(
    entityObject,
    "SourceObjectOfRecordDisplayName",
    "sourceObjectOfRecordDisplayName",
    "SourceObjectOfRecordName",
    "sourceObjectOfRecordName",
    "SourceObjectDisplayName",
    "sourceObjectDisplayName",
    "SourceObjectName",
    "sourceObjectName"
  );
  if (directName) return directName;
  const sourceObjectId = getEntityObjectSourceObjectOfRecordId(entityObject);
  return sourceObjectId ? sourceObjectsById.get(sourceObjectId.toLowerCase()) || "" : "";
};
const loadSourceObjectSelectData = async (sourceObjectMgr) => {
  if (!sourceObjectMgr?.GetSelectData) return [];
  const result = await sourceObjectMgr.GetSelectData(SOURCE_OBJECT_SELECT_DATA_FIELD);
  return asArray(result?.jsonData);
};
const getEntitySelectorHeaderStatus = (row) => {
  const active = readValue(row, "IsActive", "isActive") !== false;
  return {
    title: active ? "Active" : "Inactive",
    tone: active ? "active" : "inactive"
  };
};

const validateDuplicateEntityDeployments = (rows) => {
  const seen = new Set();
  for (const row of asArray(rows).filter((item) => readValue(item, "IsDeleted", "isDeleted") !== true)) {
    const key = pairKey(row);
    if (key === ":") continue;
    if (seen.has(key)) {
      return "Each entity object can only be assigned once per deployment.";
    }
    seen.add(key);
  }
  return "";
};

const DestinationEntityDeploymentConfiguratorStep = ({
  context,
  onSave,
  onContinue,
  registerStepController,
  onDirty
}) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const productResourceDefaults = useMemo(
    () => getDestinationSetupDefaults(wizard, context.productResourceDefaults),
    [context.productResourceDefaults, wizard]
  );
  const managerKey = getDestinationManagerKey(productResourceDefaults);
  const destinationMgr = resourceDomainContext?.managers?.[managerKey];
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const sourceObjectMgr = resourceDomainContext?.managers?.sourceObject;
  const savedValues = getSavedEntityDeploymentStepValues(wizard, values);
  const destinationId = getDestinationIdFromValues(wizard, savedValues, productResourceDefaults);
  const destinationLabel = getDestinationLabel(productResourceDefaults);
  const profileMetadataReady = isCoreProfileMetadataReady(coreContext);
  const [destination, setDestination] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [entityDeployments, setEntityDeployments] = useState([]);
  const [entityObjects, setEntityObjects] = useState([]);
  const [sourceObjectsById, setSourceObjectsById] = useState(() => new Map());
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(Boolean(destinationId));
  const [localError, setLocalError] = useState("");
  const [selectedEntityDeploymentKey, setSelectedEntityDeploymentKey] = useState("");
  const [cardEditorOpen, setCardEditorOpen] = useState(false);
  const [entitySelectorOpen, setEntitySelectorOpen] = useState(false);
  const [selectedEntityObjectIds, setSelectedEntityObjectIds] = useState([]);
  const destinationMgrRef = useRef(destinationMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const sourceObjectMgrRef = useRef(sourceObjectMgr);
  const coreContextRef = useRef(coreContext);
  const coreStateRef = useRef(coreState);
  const destinationRef = useRef(destination);
  const deploymentsRef = useRef(deployments);
  const entityObjectsRef = useRef(entityObjects);
  const entityDeploymentsRef = useRef(entityDeployments);
  const productResourceDefaultsRef = useRef(productResourceDefaults);
  const loadKeyRef = useRef("");
  const draftSequenceRef = useRef(0);
  const pendingEntityDeploymentKeysRef = useRef(new Set());
  const isBusyRef = useRef(false);

  destinationMgrRef.current = destinationMgr;
  entityObjectMgrRef.current = entityObjectMgr;
  sourceObjectMgrRef.current = sourceObjectMgr;
  coreContextRef.current = coreContext;
  coreStateRef.current = coreState;
  destinationRef.current = destination;
  deploymentsRef.current = deployments;
  entityObjectsRef.current = entityObjects;
  entityDeploymentsRef.current = entityDeployments;
  productResourceDefaultsRef.current = productResourceDefaults;
  isBusyRef.current = loading;

  useEffect(() => {
    let mounted = true;
    const activeDestinationMgr = destinationMgrRef.current;
    const activeEntityObjectMgr = entityObjectMgrRef.current;
    const loadKey = `${managerKey}:${destinationId || "none"}:entity-deployments`;
    if (!destinationId) {
      setDestination(null);
      setDeployments([]);
      setEntityObjects([]);
      setEntityDeployments([]);
      setLoading(false);
      setLocalError("");
      return undefined;
    }
    if (!activeDestinationMgr) {
      setLoading(false);
      setLocalError(`${destinationLabel} service is not available.`);
      return undefined;
    }
    if (!activeEntityObjectMgr?.Query) {
      setLoading(false);
      setLocalError("Entity Object service is not available.");
      return undefined;
    }
    if (!hasLoadedManagerMetadata(activeDestinationMgr) && !isCoreProfileMetadataReady(coreContextRef.current)) {
      setLoading(true);
      setLocalError("");
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;
    loadKeyRef.current = loadKey;
    setLoading(true);
    setLocalError("");

    const load = async () => {
      try {
        const [loadedDestination, metadataResult] = await Promise.all([
          loadDestinationById({
            manager: activeDestinationMgr,
            destinationId,
            coreState: coreStateRef.current,
            fallbackMessage: `${destinationLabel} could not be loaded.`
          }),
          loadDestinationManagerMetadata({
            manager: activeDestinationMgr,
            coreContext: coreContextRef.current
          })
        ]);
        if (!loadedDestination) throw new Error(`The linked ${destinationLabel} could not be loaded.`);
        const activeSchemaVersionId = readValue(loadedDestination, "ActiveSchemaVersionId", "activeSchemaVersionId") || "";
        const entityObjectResult = activeSchemaVersionId
          ? await activeEntityObjectMgr.Query({
            model: "x => x.IsDeleted == false && x.SchemaVersionId == schemaVersionId",
            params: { schemaVersionId: activeSchemaVersionId }
          })
          : { Items: [] };
        if (!mounted) return;
        requireServiceResponse(metadataResult, "Entity Deployment metadata could not be loaded.");
        requireSuccessfulServiceResponse(entityObjectResult, "Entity Objects could not be loaded.", coreStateRef.current);
        const resolvedDeployments = getDestinationDeploymentRows(loadedDestination, productResourceDefaultsRef.current);
        const resolvedEntityObjects = getOperationItems(entityObjectResult)
          .filter((entityObject) => readValue(entityObject, "IsDeleted", "isDeleted") !== true)
          .sort((left, right) => getEntityObjectDisplayName(left).localeCompare(getEntityObjectDisplayName(right)));
        const resolvedSourceObjects = await loadSourceObjectSelectData(sourceObjectMgrRef.current);
        const resolvedMetadata = resolveMetadataItem(metadataResult, ENTITY_OBJECT_DEPLOYMENT_ITEM_NAME)
          || resolveMetadataItem(activeDestinationMgr.uiMetadata, ENTITY_OBJECT_DEPLOYMENT_ITEM_NAME);
        setMetadata(resolvedMetadata);
        setDestination(loadedDestination);
        setDeployments(resolvedDeployments);
        setEntityObjects(resolvedEntityObjects);
        setSourceObjectsById(buildSourceObjectsById(resolvedSourceObjects));
        pendingEntityDeploymentKeysRef.current.clear();
        setEntityDeployments(getEntityDeploymentRows(loadedDestination)
          .map((row) => normalizeEntityDeploymentRow({
            row,
            entityObjects: resolvedEntityObjects,
            deployments: resolvedDeployments,
            defaults: productResourceDefaultsRef.current
          })));
        setSelectedEntityDeploymentKey("");
        setCardEditorOpen(false);
      } catch (ex) {
        if (mounted) setLocalError(ex?.message || "Entity Deployments could not be loaded.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) loadKeyRef.current = "";
    };
  }, [destinationId, destinationLabel, managerKey, profileMetadataReady]);

  const entityOptions = useMemo(() => getEntityObjectOptions(entityObjects), [entityObjects]);
  const deploymentOptions = useMemo(
    () => getDeploymentOptions(deployments, productResourceDefaults),
    [deployments, productResourceDefaults]
  );
  const formMetadata = useMemo(
    () => buildEntityDeploymentFormMetadata({ metadata, entityOptions, deploymentOptions }),
    [deploymentOptions, entityOptions, metadata]
  );
  const formFields = useMemo(() => getMetadataFields(formMetadata), [formMetadata]);
  const cardFields = useMemo(
    () => {
      const fieldByName = new Map(formFields.map((field) => [String(readValue(field, "FieldName", "fieldName") || ""), field]));
      return ["EntityDisplayName", "DestinationState", "SchemaDestinationName", "LastSyncEndDate"]
        .map((fieldName) => fieldByName.get(fieldName))
        .filter(Boolean);
    },
    [formFields]
  );
  const entitySelectorRows = useMemo(() => entityObjects.map((entityObject) => {
    const entityObjectId = getEntityObjectRecordId(entityObject);
    return {
      ...entityObject,
      EntitySelectorKey: entityObjectId,
      EntitySelectorName: getEntityObjectDisplayName(entityObject),
      EntitySelectorObject: readValue(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "Name", "name") || entityObjectId,
      EntitySelectorSourceObjectOfRecord: getEntityObjectSourceObjectOfRecordDisplayName(entityObject, sourceObjectsById)
    };
  }), [entityObjects, sourceObjectsById]);
  const existingEntityObjectIds = useMemo(() => new Set(entityDeployments
    .filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
    .map((row) => normalizeKey(readValue(row, "EntityObjectId", "entityObjectId")))
    .filter(Boolean)), [entityDeployments]);
  const selectedEntityObjectIdSet = useMemo(
    () => new Set(selectedEntityObjectIds.map((entry) => normalizeKey(entry)).filter(Boolean)),
    [selectedEntityObjectIds]
  );
  const availableEntitySelectorIds = useMemo(() => entitySelectorRows
    .filter((row) => !existingEntityObjectIds.has(normalizeKey(getEntityObjectRecordId(row))))
    .map((row) => normalizeKey(getEntityObjectRecordId(row) || row.EntitySelectorKey))
    .filter(Boolean),
  [entitySelectorRows, existingEntityObjectIds]);
  const allAvailableEntitySelectorsSelected = availableEntitySelectorIds.length > 0
    && availableEntitySelectorIds.every((id) => selectedEntityObjectIdSet.has(id));
  const selectAllEntitySelectors = useCallback(() => {
    setSelectedEntityObjectIds(availableEntitySelectorIds);
  }, [availableEntitySelectorIds]);
  const unselectAllEntitySelectors = useCallback(() => {
    setSelectedEntityObjectIds([]);
  }, []);
  const entitySelectorToolbarLeadingContent = useMemo(() => (
    <div className="resource-wizard__entity-scope-selector-toolbar">
      <span>{selectedEntityObjectIdSet.size} selected</span>
      <div className="resource-wizard__entity-scope-selector-actions">
        {allAvailableEntitySelectorsSelected ? (
          <ButtonControl
            type="cancel-small"
            onClick={unselectAllEntitySelectors}
          >
            Unselect All
          </ButtonControl>
        ) : (
          <ButtonControl
            type="cancel-small"
            onClick={selectAllEntitySelectors}
            disabled={!availableEntitySelectorIds.length}
          >
            Select All
          </ButtonControl>
        )}
      </div>
    </div>
  ), [
    allAvailableEntitySelectorsSelected,
    availableEntitySelectorIds.length,
    selectAllEntitySelectors,
    selectedEntityObjectIdSet.size,
    unselectAllEntitySelectors
  ]);

  const openEntitySelector = useCallback(() => {
    if (readOnly || saving || loading || !destinationRef.current || !entityObjectsRef.current.length || !deploymentsRef.current.length) return;
    const existingIds = entityDeploymentsRef.current
      .filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
      .map((row) => normalizeKey(readValue(row, "EntityObjectId", "entityObjectId")))
      .filter(Boolean);
    setSelectedEntityObjectIds([...new Set(existingIds)]);
    setEntitySelectorOpen(true);
  }, [loading, readOnly, saving]);

  const closeEntitySelector = useCallback(() => {
    setEntitySelectorOpen(false);
    setSelectedEntityObjectIds([]);
  }, []);

  const generateSelectedEntityDeployments = useCallback(() => {
    const selectedIds = new Set(selectedEntityObjectIds.map((entry) => normalizeKey(entry)).filter(Boolean));
    if (!selectedIds.size) {
      closeEntitySelector();
      return;
    }

    const deployment = getDefaultDeployment(deploymentsRef.current);
    const deploymentId = getDeploymentRecordId(deployment);
    if (!deployment || !deploymentId) {
      setLocalError("Add at least one deployment before configuring Entity Deployment Scope.");
      return;
    }

    const existingPairKeys = new Set(entityDeploymentsRef.current
      .filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
      .map((row) => pairKey(row)));
    const selectedEntities = entityObjectsRef.current.filter((entityObject) =>
      selectedIds.has(normalizeKey(getEntityObjectRecordId(entityObject)))
    );
    const rowsToAdd = [];
    selectedEntities.forEach((entityObject) => {
      const entityObjectId = getEntityObjectRecordId(entityObject);
      const nextPairKey = `${deploymentId}:${entityObjectId}`;
      if (!entityObjectId || existingPairKeys.has(nextPairKey)) return;
      draftSequenceRef.current += 1;
      const nextRow = normalizeEntityDeploymentRow({
        row: {
          _tempKey: `destination-entity-deployment-selection-${Date.now()}-${draftSequenceRef.current}`,
          IsNew: true,
          IsActive: true,
          IsDeleted: false,
          UniqueId: createClientGuid(),
          EntityObjectId: entityObjectId,
          EntityDisplayName: getEntityObjectDisplayName(entityObject),
          SchemaDestinationDeploymentId: deploymentId,
          SchemaDestinationName: getDeploymentDisplayName(deployment, productResourceDefaultsRef.current)
        },
        entityObjects: entityObjectsRef.current,
        deployments: deploymentsRef.current,
        defaults: productResourceDefaultsRef.current
      });
      rowsToAdd.push(nextRow);
      pendingEntityDeploymentKeysRef.current.add(String(getDestinationRowKey(nextRow)));
      existingPairKeys.add(nextPairKey);
    });

    if (rowsToAdd.length) {
      onDirty?.();
      setEntityDeployments((previous) => [...previous, ...rowsToAdd]);
      setSelectedEntityDeploymentKey("");
    }
    closeEntitySelector();
  }, [closeEntitySelector, onDirty, selectedEntityObjectIds]);

  const updateEntityDeployment = useCallback((updatedRow) => {
    const nextRow = normalizeEntityDeploymentRow({
      row: updatedRow,
      entityObjects: entityObjectsRef.current,
      deployments: deploymentsRef.current,
      defaults: productResourceDefaultsRef.current
    });
    const updatedKey = String(getDestinationRowKey(nextRow));
    pendingEntityDeploymentKeysRef.current.delete(updatedKey);
    onDirty?.();
    setEntityDeployments((previous) => previous.map((row, index) =>
      String(getDestinationRowKey(row, index)) === updatedKey
        ? nextRow
        : row
    ));
  }, [onDirty]);

  const cancelEntityDeploymentEdit = useCallback((row, key) => {
    const entityDeploymentKey = String(key || getDestinationRowKey(row));
    if (!pendingEntityDeploymentKeysRef.current.has(entityDeploymentKey)) return;
    pendingEntityDeploymentKeysRef.current.delete(entityDeploymentKey);
    setEntityDeployments((previous) => previous.filter((candidate, index) =>
      String(getDestinationRowKey(candidate, index)) !== entityDeploymentKey
    ));
    setSelectedEntityDeploymentKey("");
  }, []);

  const hardDeleteEntityDeployment = useCallback((row, key) => {
    if (readOnly) return;
    const entityDeploymentKey = String(key || getDestinationRowKey(row));
    pendingEntityDeploymentKeysRef.current.delete(entityDeploymentKey);
    onDirty?.();
    setEntityDeployments((previous) => previous.filter((candidate, index) =>
      String(getDestinationRowKey(candidate, index)) !== entityDeploymentKey
    ));
    setSelectedEntityDeploymentKey("");
    setCardEditorOpen(false);
  }, [onDirty, readOnly]);

  const saveEntityDeployments = useCallback(async (currentValues = values) => {
    const activeMgr = destinationMgrRef.current;
    const currentDestination = destinationRef.current;
    if (!currentDestination) throw new Error(`Complete the ${destinationLabel} step before configuring Entity Deployments.`);
    if (!deploymentsRef.current.length) throw new Error("Add at least one deployment before configuring Entity Deployment Scope.");
    if (!entityObjectsRef.current.length) throw new Error("No Entity Objects are available for the selected active schema version.");
    const rowsToSave = entityDeploymentsRef.current
      .map((row) => normalizeEntityDeploymentRow({
        row,
        entityObjects: entityObjectsRef.current,
        deployments: deploymentsRef.current,
        defaults: productResourceDefaultsRef.current
      }));
    const requiredMessage = validateEntityDeploymentRows(rowsToSave, formFields, "Entity Deployment");
    const duplicateMessage = validateDuplicateEntityDeployments(rowsToSave);
    const validationMessage = requiredMessage || duplicateMessage;
    if (validationMessage) {
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    setLocalError("");
    const deploymentRowsToSave = deploymentsRef.current
      .map((row) => normalizeDeploymentRow(row, currentDestination, productResourceDefaultsRef.current));
    const destinationToSave = setEntityDeploymentRows(
      buildDestinationDeploymentSavePayload(currentDestination, productResourceDefaultsRef.current, deploymentRowsToSave),
      rowsToSave
    );
    const savedDestination = await saveDestination({
      manager: activeMgr,
      destination: {
        ...destinationToSave,
        IsNew: false
      },
      coreState: coreStateRef.current,
      fallbackMessage: "Entity Deployments could not be saved."
    });
    const savedRows = getEntityDeploymentRows(savedDestination)
      .map((row) => normalizeEntityDeploymentRow({
        row,
        entityObjects: entityObjectsRef.current,
        deployments: deploymentsRef.current,
        defaults: productResourceDefaultsRef.current
      }));
    pendingEntityDeploymentKeysRef.current.clear();
    setDestination(savedDestination);
    setEntityDeployments(savedRows);
    return buildEntityDeploymentStepValues({
      currentValues,
      destination: savedDestination,
      entityDeployments: savedRows
    });
  }, [destinationLabel, formFields, values]);

  useEffect(() => {
    registerStepController?.({
      get isBusy() {
        return Boolean(isBusyRef.current);
      },
      prepareSave: saveEntityDeployments,
      prepareContinue: saveEntityDeployments
    });
    return () => registerStepController?.(null);
  }, [registerStepController, saveEntityDeployments]);

  const toolbarActions = useMemo(() => (
    readOnly ? [] : [{
      Key: "add-entity-deployment",
      Title: "Add Entity Deployments",
      Label: "Add Entity Deployments",
      IconKey: "add",
      Disabled: readOnly || saving || loading || !destination || !entityObjects.length || !deployments.length,
      ActionCallback: openEntitySelector
    }]
  ), [deployments.length, destination, entityObjects.length, loading, openEntitySelector, readOnly, saving]);

  const selectedHeaderIconActions = useMemo(() => (
    readOnly ? [] : [{
      Key: "hard-delete-entity-deployment",
      Title: "Delete Entity Deployment",
      IconKey: "delete",
      Tone: "danger",
      Disabled: saving || loading,
      ActionCallback: hardDeleteEntityDeployment
    }]
  ), [hardDeleteEntityDeployment, loading, readOnly, saving]);

  const valueDisplayResolvers = useMemo(() => ({
    EntityObjectId: (value) => entityOptions.find((option) => String(option.Value) === String(value))?.Text || value,
    SchemaDestinationDeploymentId: (value) => deploymentOptions.find((option) => String(option.Value) === String(value))?.Text || value,
    DestinationState: (value) => {
      const option = [
        { Text: "Unknown", Value: "0" },
        { Text: "Normal", Value: "1" },
        { Text: "Error", Value: "99" }
      ].find((entry) => String(entry.Value) === String(value));
      return option?.Text || value;
    },
    DisableTruncate: (value) => (value === false ? "No" : "Yes"),
    ForceReconcile: (value) => (value ? "Yes" : "No"),
    FilePackagingMode: (value) => (String(value) === "2" ? "Per Document" : "Batch")
  }), [deploymentOptions, entityOptions]);

  const entityDeploymentCardTitleResolver = useCallback((row) =>
    readValue(row, "EntityDisplayName", "entityDisplayName", "EntityObjectName", "entityObjectName", "Name", "name") || "",
  []);

  const renderCardGrid = () => {
    if (!destinationId) {
      return <div className="resource-wizard__state">Complete the Destination step before configuring Entity Deployment Scope.</div>;
    }
    if (loading) {
      return <div className="resource-wizard__linked-step-loading"><Loading /></div>;
    }
    if (!destination) {
      return <div className="resource-wizard__state">The linked {destinationLabel} is not available.</div>;
    }
    if (!deployments.length) {
      return <div className="resource-wizard__state">Add at least one deployment before configuring Entity Deployment Scope.</div>;
    }
    if (!entityObjects.length) {
      return <div className="resource-wizard__state">No Entity Objects are available for the selected active schema version.</div>;
    }
    return (
      <CardGridControl
        rows={entityDeployments}
        metadata={formMetadata}
        formMetadata={formMetadata}
        formFields={formFields}
        cardFields={cardFields}
        rowKey={getDestinationRowKey}
        selectedRowKey={selectedEntityDeploymentKey}
        onSelectedRowKeyChange={setSelectedEntityDeploymentKey}
        onEditingChange={setCardEditorOpen}
        onCancelEdit={cancelEntityDeploymentEdit}
        onRowChange={updateEntityDeployment}
        toolbarActions={toolbarActions}
        selectedHeaderIconActions={selectedHeaderIconActions}
        cardTitleResolver={entityDeploymentCardTitleResolver}
        readOnly={readOnly}
        disabled={saving}
        toggleEnabled={true}
        toggleFieldName="IsActive"
        activeFilterFieldName="IsActive"
        valueDisplayResolvers={valueDisplayResolvers}
        emptyContent="No Entity Deployment Scope rows are configured yet."
        minCardWidth="15rem"
      />
    );
  };

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title="Entity Deployment Scope"
        description={`Choose which Entity Objects this ${destinationLabel} will load, deliver, or sync.`}
        countText={`${entityDeployments.length} configured`}
      />
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      <div className="resource-wizard__linked-record-catalog resource-wizard__linked-record-catalog--compact">
        <div className="resource-wizard__linked-record-catalog-scroll">
          {renderCardGrid()}
        </div>
      </div>
      {!cardEditorOpen ? (
        <div className="resource-wizard__linked-step-actions">
          <div />
          <div className="resource-wizard__linked-step-actions-right">
            <ButtonControl
              type="standard-small"
              className="resource-wizard__button resource-wizard__button--secondary"
              onClick={() => onSave?.()}
              disabled={readOnly || saving || loading || !destination || !deployments.length || !entityObjects.length}
              loading={saving}
            >
              Save
            </ButtonControl>
            <ButtonControl
              type="ok-small"
              className="resource-wizard__button resource-wizard__button--primary"
              onClick={() => onContinue?.()}
              disabled={readOnly || saving || loading || !destination || !deployments.length || !entityObjects.length}
              loading={saving}
            >
              Save & Continue
            </ButtonControl>
          </div>
        </div>
      ) : null}
      <DialogControl
        openDialog={entitySelectorOpen}
        title="Add Entity Deployment Scope"
        okText="Add Selected"
        cancelText="Cancel"
        onOk={generateSelectedEntityDeployments}
        onCancel={closeEntitySelector}
        disableOk={!selectedEntityObjectIds.length || readOnly || saving || loading}
      >
        <div className="resource-wizard__entity-scope-selector">
          <CardGridControl
            rows={entitySelectorRows}
            metadata={ENTITY_SELECTOR_METADATA}
            rowKey={(row) => getEntityObjectRecordId(row) || row.EntitySelectorKey}
            selectedRowKeys={selectedEntityObjectIds}
            onSelectedRowKeysChange={setSelectedEntityObjectIds}
            selectableRowDisabledResolver={(row) => existingEntityObjectIds.has(normalizeKey(getEntityObjectRecordId(row)))}
            selectable={true}
            multiSelect={true}
            headerStatusResolver={getEntitySelectorHeaderStatus}
            activeFilterDefaultMode="both"
            activeFilterOptions={[
              { key: "available", label: "Available" },
              { key: "selected", label: "Selected" },
              { key: "both", label: "Both" }
            ]}
            activeFilterRowPredicate={(row, { mode, isSelected }) => {
              if (mode === "selected") return isSelected;
              if (mode === "both") return true;
              return !existingEntityObjectIds.has(normalizeKey(getEntityObjectRecordId(row)));
            }}
            toolbarLeadingContent={entitySelectorToolbarLeadingContent}
            emptyContent="No Entity Objects are available for the selected active schema version."
            minCardWidth="15rem"
            maxColumns={3}
          />
        </div>
      </DialogControl>
    </div>
  );
};

export default DestinationEntityDeploymentConfiguratorStep;
